<script setup lang="ts">
/**
 * One generated Shape control (plan §4.4): rendered from a family parameter's metadata —
 * `length` → field with limits; `count` → stepper; `enum` → choice; `perBay` → the bay-width
 * rows; `perOpening` → the openings list. The family's behaviours (even, rebalance, remainder)
 * live in the family's change functions (src/families/grid-bookcase/design.ts), reached
 * through src/app/edits.ts; this component only renders and routes.
 */
import { computed } from 'vue';
import type { ParameterDef } from '@/families/types';
import { bayCount, fmtMm, layoutOf, parseMm } from '@/domain';
import Icon from './Icon.vue';
import ProvChip from './ProvChip.vue';
import DimensionField from './DimensionField.vue';
import OpeningList from './OpeningList.vue';
import BayWidthList from './BayWidthList.vue';
import TierEndsList from './TierEndsList.vue';
import RowListControl from './RowListControl.vue';
import BoardListControl from './BoardListControl.vue';
import FrontsControl from './FrontsControl.vue';
import { snap, ui } from '@/app/state';
import { commitPlinth, commitShelves, setBackTo, setBaysTo, setDesignKey, setFamilyParam, setJoineryTo } from '@/app/edits';
import { focusSoon } from '@/app/navigation';
import { paramValue } from '@/app/param-value';

const props = defineProps<{ p: ParameterDef; hideLabel?: boolean }>();
const d = computed(() => snap.value.design);
const key = computed(() => props.p.key);
const status = computed(() => (props.p.fieldKey ? snap.value.fields[props.p.fieldKey]?.status : undefined));

/** Current value of a parameter, from the family's design. */
const value = computed<unknown>(() => {
  const x = d.value;
  // families 2–4: the family's params, else the parameter's default (new optional keys are absent until set)
  if (x.params && !['W', 'H', 'D'].includes(key.value)) return paramValue(x, key.value) ?? props.p.default;
  switch (key.value) {
    case 'base': return paramValue(x, 'base');
    case 'bays': return bayCount(x);
    case 'shelfCount': return layoutOf(x, ui.bay).n;
    case 'back': return x.back ?? 'overlay';
    case 'plinth': return x.plinth ?? 0;
    case 'joinery': return x.joinery ?? 'screwed-butt';
    default: return (x as Record<string, unknown>)[key.value] ?? props.p.default;
  }
});
/** Families 2–4 keep their parameters in design.params and change them through the family (present or not yet). */
const familyOwned = computed(() => !!d.value.params && !['W', 'H', 'D'].includes(key.value));
/** Any other parameter: the family when it owns it, else a top-level design key (grid: base, sideStyle…). */
function setAny(v: unknown, label: string): boolean {
  return familyOwned.value ? setFamilyParam(key.value, v, label) : setDesignKey(key.value, v, label);
}
const range = computed(() => props.p.range ?? { min: 0, max: 99 });
const multi = computed(() => bayCount(d.value) > 1);

function step(delta: number, act: string) {
  focusSoon(`[data-act="${act}"]`);
  const next = Number(value.value) + delta;
  if (familyOwned.value) setFamilyParam(key.value, next, `${next} ${props.p.ui.label.toLowerCase()}`);
  else if (key.value === 'bays') setBaysTo(next);
  else if (key.value === 'shelfCount') commitShelves(String(next), '', ui.bay);
  else setAny(next, `${next} ${props.p.ui.label.toLowerCase()}`);
}
function choose(v: string) {
  focusSoon(`[data-param="${key.value}"] [data-value="${v}"]`);
  if (familyOwned.value) setFamilyParam(key.value, v, `${props.p.ui.label}: ${props.p.options?.find((o) => o.value === v)?.label ?? v}`);
  else if (key.value === 'back') setBackTo(v as 'none' | 'overlay' | 'rebated');
  else if (key.value === 'joinery') setJoineryTo(v as 'screwed-butt' | 'housed-dado');
  else setAny(v, `${props.p.ui.label}: ${props.p.options?.find((o) => o.value === v)?.label ?? v}`);
}
const draftId = computed(() => `param-${key.value}`);
const fieldValue = computed(() => (draftId.value in ui.drafts ? ui.drafts[draftId.value]! : fmtMm(Number(value.value) || 0)));
function onInput(e: Event) { ui.drafts[draftId.value] = (e.target as HTMLInputElement).value; }
/** A length: the grid's plinth, or any family-owned length (ticks), checked against its range. */
function commitLength(raw: string) {
  if (key.value === 'plinth' && !familyOwned.value) { commitPlinth(raw, draftId.value); return; }
  const r = parseMm(raw);
  if (!r.ok) { ui.fieldErrors[key.value] = r.error; return; }
  const rg = props.p.range;
  if (rg && (r.ticks < rg.min || r.ticks > rg.max)) { ui.fieldErrors[key.value] = `${props.p.ui.label} must be between ${fmtMm(rg.min)} and ${fmtMm(rg.max)} mm.`; return; }
  if (setAny(r.ticks, `${props.p.ui.label} ${fmtMm(r.ticks)} mm`)) delete ui.drafts[draftId.value];
}
/** A boolean family parameter: a switch. */
function toggle(e: Event) {
  const on = (e.target as HTMLInputElement).checked;
  focusSoon(`#sw-${key.value}`);
  setAny(on, `${props.p.ui.label} ${on ? 'on' : 'off'}`);
}
/** A ratio (0–1): a slider in whole percent. */
function setRatio(e: Event) {
  const v = Number((e.target as HTMLInputElement).value) / 100;
  setAny(v, `${props.p.ui.label} ${Math.round(v * 100)} %`);
}
/** A per-row list of ratio lists (offset-grid dividers): editable rows, not a read-only list. */
const isRowRatios = computed(() => !!d.value.params && props.p.ui.control === 'rows'
  && (value.value == null || (Array.isArray(value.value) && (value.value as unknown[]).every((r) => Array.isArray(r)))));
function onKey(e: KeyboardEvent) {
  const el = e.target as HTMLInputElement;
  if (e.key === 'Enter') { e.preventDefault(); commitLength(el.value); return; }
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const r = parseMm(el.value);
  if (!r.ok) return;
  e.preventDefault();
  const next = fmtMm(Math.max(0, r.ticks + (e.shiftKey ? 100 : 10) * (e.key === 'ArrowUp' ? 1 : -1)));
  ui.drafts[draftId.value] = next;
  el.value = next;
  commitLength(next);
}
const errKey = computed(() => (key.value === 'shelfCount' ? 'n' : key.value));
const err = computed(() => ui.fieldErrors[errKey.value] ?? null);
const countLabel = computed(() => {
  const n = Number(value.value);
  if (key.value === 'bays') return n === 1 ? 'bay' : 'bays';
  if (key.value === 'shelfCount') return n === 1 ? 'shelf' : 'shelves';
  const l = props.p.ui.label.toLowerCase();
  return n === 1 ? l.replace(/s$/, '') : l;
});
const stepLabel = (more: boolean) => {
  if (key.value === 'bays') return more ? 'One more bay' : 'One fewer bay';
  if (key.value === 'shelfCount') return more ? 'One more shelf' : 'One fewer shelf';
  const one = props.p.ui.label.toLowerCase().replace(/s$/, '');
  return more ? `One more ${one}` : `One fewer ${one}`;
};
/** Offset-grid divider positions, per row bottom-up (mm from the left). */
const rowLists = computed(() => {
  const v = value.value;
  if (!Array.isArray(v)) return [];
  return (v as number[][]).map((xs, i) => ({ row: i + 1, text: xs.length ? xs.map((x) => `${Math.round((d.value.W / 10) * x)}`).join(', ') + ' mm' : 'no divider' }));
});
</script>

<template>
  <div class="param" :data-param="key">
    <DimensionField v-if="key === 'W' || key === 'H' || key === 'D'" :k="key" />

    <template v-else-if="p.type === 'count'">
      <div class="sec-head"><h3 :id="`ph-${key}`" class="h-sub" :class="{ 'sr-only': hideLabel && !(key === 'shelfCount' && multi) }">{{ p.ui.label }}{{ key === 'shelfCount' && multi ? ` · bay ${ui.bay + 1}` : '' }}</h3><ProvChip v-if="status" :status="status" compact /></div>
      <div class="stepper" role="group" :aria-labelledby="`ph-${key}`">
        <button type="button" class="icon-btn" :data-act="key === 'shelfCount' ? 'n-' : `${key}-`" :aria-label="stepLabel(false)" :disabled="Number(value) <= range.min" @click="step(-1, key === 'shelfCount' ? 'n-' : `${key}-`)"><Icon name="minus" /></button>
        <output :id="key === 'shelfCount' ? 'nOut' : `${key}Out`" class="stepper-v">{{ value }}</output><span class="stepper-u">{{ countLabel }}</span>
        <button type="button" class="icon-btn" :data-act="key === 'shelfCount' ? 'n+' : `${key}+`" :aria-label="stepLabel(true)" :disabled="Number(value) >= range.max" @click="step(1, key === 'shelfCount' ? 'n+' : `${key}+`)"><Icon name="plus" /></button>
        <p v-if="p.ui.help" class="hint">{{ p.ui.help }}</p>
      </div>
      <p v-if="err" :id="`err-${errKey}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
    </template>

    <template v-else-if="p.type === 'enum'">
      <div class="sec-head"><h3 :id="`ph-${key}`" class="h-sub">{{ p.ui.label }}</h3><ProvChip v-if="status" :status="status" compact /></div>
      <div class="seg seg--sm seg--param" role="radiogroup" :aria-labelledby="`ph-${key}`">
        <button v-for="o in p.options" :key="o.value" type="button" role="radio" :aria-checked="value === o.value" :data-value="o.value" :title="o.help" @click="choose(o.value)">{{ o.label }}</button>
      </div>
      <p class="hint">{{ p.options?.find((o) => o.value === value)?.help }}</p>
    </template>

    <template v-else-if="p.type === 'length'">
      <div class="dim" :class="{ 'has-err': !!err }">
        <div class="dim-top"><label class="lbl" :for="draftId">{{ p.ui.label }}</label><ProvChip v-if="status" :status="status" compact /></div>
        <div class="input-unit">
          <input
            :id="draftId"
            class="input mono"
            inputmode="decimal"
            autocomplete="off"
            :value="fieldValue"
            :aria-describedby="`hint-${key}${err ? ` err-${key}` : ''}`"
            :aria-invalid="err ? 'true' : undefined"
            @input="onInput"
            @change="($event.target as HTMLInputElement).isConnected && commitLength(($event.target as HTMLInputElement).value)"
            @keydown="onKey"
          ><span>mm</span>
        </div>
        <p :id="`hint-${key}`" class="hint">{{ p.ui.help }}</p>
      </div>
      <p v-if="err" :id="`err-${key}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
    </template>

    <template v-else-if="p.type === 'boolean'">
      <label class="switch" :data-param-switch="key"><input :id="`sw-${key}`" type="checkbox" role="switch" :checked="!!value" @change="toggle"><span class="sw" aria-hidden="true" /><span>{{ p.ui.label }}</span></label>
      <p v-if="p.ui.help" class="hint">{{ p.ui.help }}</p>
      <p v-if="err" :id="`err-${key}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
    </template>

    <template v-else-if="p.type === 'ratio'">
      <label class="lbl" :for="`rg-${key}`">{{ p.ui.label }} <span class="muted">{{ Math.round(Number(value ?? 0) * 100) }} %</span></label>
      <input :id="`rg-${key}`" type="range" class="range" :min="Math.round((p.range?.min ?? 0) * 100)" :max="Math.round((p.range?.max ?? 1) * 100)" step="1" :value="Math.round(Number(value ?? 0) * 100)" @change="setRatio">
      <p v-if="p.ui.help" class="hint">{{ p.ui.help }}</p>
      <p v-if="err" :id="`err-${key}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
    </template>

    <BayWidthList v-else-if="p.type === 'perBay'" :p="p" />
    <TierEndsList v-else-if="p.type === 'perTier'" :p="p" />
    <FrontsControl v-else-if="p.ui.control === 'fronts'" :p="p" />
    <BoardListControl v-else-if="p.type === 'list' && p.ui.control === 'boards'" :p="p" />
    <RowListControl v-else-if="p.type === 'list' && isRowRatios" :p="p" />
    <template v-else-if="p.type === 'list'">
      <h3 class="h-sub">{{ p.ui.label }}</h3>
      <ol class="rowlist" reversed>
        <li v-for="r in rowLists.slice().reverse()" :key="r.row" :data-row="r.row"><span class="muted">Row {{ r.row }}</span> {{ r.text }}</li>
      </ol>
      <p v-if="p.ui.help" class="hint">{{ p.ui.help }}</p>
    </template>
    <OpeningList v-else-if="p.type === 'perOpening'" />
  </div>
</template>
